import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Map, Sparkles, BookOpen, Brain, ArrowRight, Lightbulb, AlertTriangle, ChevronRight } from "lucide-react";
import { motion } from "framer-motion";

const IMPORTANCE_COLORS = { high: "bg-neo-red/20 border-neo-red", medium: "bg-neo-yellow/20 border-neo-yellow", low: "bg-neo-blue/20 border-neo-blue" };

export default function RoadmapView({ api, paperId }) {
  const [roadmap, setRoadmap] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fetched, setFetched] = useState(false);

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await axios.get(`${api}/papers/${paperId}/roadmap`, { withCredentials: true });
        if (res.data) setRoadmap(res.data);
      } catch { /* no roadmap yet */ }
      setFetched(true);
    };
    fetch();
  }, [api, paperId]);

  const generateRoadmap = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${api}/papers/${paperId}/roadmap`, {}, { withCredentials: true });
      setRoadmap(res.data);
      toast.success("Roadmap generated!");
    } catch {
      toast.error("Failed to generate roadmap");
    } finally {
      setLoading(false);
    }
  };

  if (!fetched) return null;

  if (!roadmap) {
    return (
      <div className="neo-card dark:bg-[#1E1E1E] dark:border-[#444] dark:shadow-[4px_4px_0px_0px_#444] text-center py-6" data-testid="roadmap-empty">
        <Map className="w-10 h-10 text-neo-purple mx-auto mb-2" />
        <p className="font-heading font-bold dark:text-white mb-1">AI Reading Roadmap</p>
        <p className="text-xs text-neo-text-secondary dark:text-gray-400 mb-3">Get an AI-powered overview of this paper's structure, key concepts, and reading tips</p>
        <button onClick={generateRoadmap} disabled={loading} className={`neo-btn text-sm ${loading ? "bg-gray-200 text-gray-500 border-gray-300 shadow-none" : "neo-btn-purple"}`} data-testid="generate-roadmap-btn">
          {loading ? (
            <span className="flex items-center gap-2"><div className="w-4 h-4 border-2 border-neo-text border-t-transparent rounded-full animate-spin" /> Analyzing paper...</span>
          ) : (
            <span className="flex items-center gap-2"><Sparkles className="w-4 h-4" /> Generate Roadmap</span>
          )}
        </button>
      </div>
    );
  }

  return (
    <div className="neo-card dark:bg-[#1E1E1E] dark:border-[#444] dark:shadow-[4px_4px_0px_0px_#444] space-y-4" data-testid="roadmap-view">
      <div className="flex items-center gap-2">
        <Map className="w-5 h-5 text-neo-purple" />
        <h3 className="font-heading font-bold dark:text-white">Reading Roadmap</h3>
      </div>

      {/* Summary */}
      <div className="p-3 rounded-lg bg-neo-purple/10 border-2 border-neo-purple/30">
        <p className="text-sm dark:text-gray-200">{roadmap.title_summary}</p>
      </div>

      {/* Difficulty + Methodology */}
      <div className="flex gap-2 flex-wrap">
        <span className={`neo-badge text-xs ${roadmap.estimated_difficulty === "hard" ? "bg-neo-red" : roadmap.estimated_difficulty === "easy" ? "bg-neo-green" : "bg-neo-yellow"}`}>
          {roadmap.estimated_difficulty || "medium"} difficulty
        </span>
        {roadmap.methodology && <span className="neo-badge text-xs bg-neo-blue">{roadmap.methodology.substring(0, 40)}</span>}
      </div>

      {/* Key Sections */}
      {roadmap.key_sections?.length > 0 && (
        <div>
          <p className="text-xs uppercase tracking-[0.15em] font-semibold text-neo-text-secondary dark:text-gray-400 mb-2">Paper Structure</p>
          <div className="space-y-1.5">
            {roadmap.key_sections.map((sec, i) => (
              <div key={i} className={`p-2 rounded-lg border-2 text-xs ${IMPORTANCE_COLORS[sec.importance] || IMPORTANCE_COLORS.medium}`}>
                <span className="font-bold dark:text-white">{sec.name}</span>
                <span className="text-neo-text-secondary dark:text-gray-400 ml-1">— {sec.description}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Key Concepts */}
      {roadmap.key_concepts?.length > 0 && (
        <div>
          <p className="text-xs uppercase tracking-[0.15em] font-semibold text-neo-text-secondary dark:text-gray-400 mb-2 flex items-center gap-1"><Brain className="w-3 h-3" /> Key Concepts</p>
          <div className="flex flex-wrap gap-1.5">
            {roadmap.key_concepts.map((c, i) => (
              <span key={i} className="neo-badge text-xs bg-neo-green/30 dark:bg-neo-green/20 dark:text-gray-200">{c}</span>
            ))}
          </div>
        </div>
      )}

      {/* Reading Tips */}
      {roadmap.reading_tips?.length > 0 && (
        <div>
          <p className="text-xs uppercase tracking-[0.15em] font-semibold text-neo-text-secondary dark:text-gray-400 mb-2 flex items-center gap-1"><Lightbulb className="w-3 h-3" /> Reading Tips</p>
          <ul className="space-y-1">
            {roadmap.reading_tips.map((tip, i) => (
              <li key={i} className="text-xs text-neo-text-secondary dark:text-gray-300 flex items-start gap-1.5">
                <ChevronRight className="w-3 h-3 mt-0.5 flex-shrink-0 text-neo-yellow" />{tip}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Transition Guide */}
      {roadmap.transition_guide && (
        <div className="p-2 rounded-lg bg-neo-yellow/10 border-2 border-neo-yellow/30">
          <p className="text-xs font-bold dark:text-white mb-0.5 flex items-center gap-1"><ArrowRight className="w-3 h-3" /> Flow Guide</p>
          <p className="text-xs text-neo-text-secondary dark:text-gray-300">{roadmap.transition_guide}</p>
        </div>
      )}
    </div>
  );
}
